import { useMemo } from "react";

import { strings } from "@perceptron/constants";
import { AnnotationType } from "@perceptron/core/types";
import { Icon, IconName } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import { isNumber, isString } from "lodash-es";

// Keep in sync with SelectOutputFormat/Content.tsx + TrackLabelPane.
const WAYPOINT_ICON: Record<"point_box" | "point" | "polygon", IconName> = {
  point_box: "box-icon",
  point: "crosshair",
  polygon: "polygon",
};

import { useStreamSource } from "@demo/contexts/StreamSourceContext";
import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import {
  formatAnnotationLabel,
  formatLabelByCounter,
  getAnnotationDomId,
  getAnnotationKey,
} from "@demo/utils/annotations";

export interface TrackAnnotationTextProps {
  children?: React.ReactNode;
}

/**
 * Inline pill for an atomic `<track>` annotation. No expand/collapse (the
 * track's inner `<point_box>` waypoints are internal data). `isKeyHighlighted`
 * is prefix-aware, so cluster-scoped highlights from the timeline/overlay
 * also light the chip.
 */
const TrackAnnotationText: React.FC<TrackAnnotationTextProps & { [key: string]: unknown }> = ({
  children: _children,
  ...props
}) => {
  const source = useStreamSource();
  const { handleMouseEnter, handleMouseLeave, handleClick, isKeyHighlighted, isKeyClicked } =
    useAnnotationInteractions();

  const isStreaming = props["data-streaming"] === "true";
  const number = props.number;
  const mention = props.mention;
  const waypointKind = props["data-waypoint-kind"];
  const iconName: IconName =
    waypointKind === "point_box" || waypointKind === "point" || waypointKind === "polygon"
      ? WAYPOINT_ICON[waypointKind]
      : "box-icon";

  const annotationKey = useMemo(() => (isNumber(number) ? getAnnotationKey(number, source) : ""), [number, source]);
  const domId = useMemo(
    () => (isNumber(number) ? `track-annotation-${getAnnotationDomId(number, source)}` : ""),
    [number, source],
  );

  const labelNumber = props["data-label-number"];
  const label = isString(mention)
    ? mention
    : isNumber(labelNumber)
      ? formatLabelByCounter(AnnotationType.TRACK, labelNumber)
      : formatAnnotationLabel(AnnotationType.TRACK, annotationKey);

  const highlighted = annotationKey ? isKeyHighlighted(annotationKey) : false;

  if (!isNumber(number)) return null;

  return (
    <span
      aria-label={formatString(strings.annotations.ariaLabels.track, label)}
      aria-pressed={isKeyClicked(annotationKey)}
      className={cn(
        "relative inline-block max-w-full animate-fade-in cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap align-bottom text-white transition-colors duration-200",
        "rounded-sm border px-1 py-0.5 label-2",
        highlighted ? "bg-[#F58210] shadow-[0_0_8px_rgba(245,130,16,0.8)]" : "bg-[#F58210]/20",
        {
          "annotation-marker-streaming border-[#A95A0B]/60": isStreaming,
          "border-[#A95A0B]": !isStreaming,
        },
      )}
      data-video-annotation-control
      id={domId}
      role="button"
      tabIndex={0}
      onBlur={() => handleMouseLeave(annotationKey)}
      onClick={(event) => {
        event.stopPropagation();
        handleClick(annotationKey, source);
      }}
      onFocus={() => handleMouseEnter([annotationKey], source)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          handleClick(annotationKey, source);
        }
      }}
      onMouseEnter={() => handleMouseEnter([annotationKey], source)}
      onMouseLeave={() => handleMouseLeave(annotationKey)}
    >
      <Icon className="text-middle mb-0.5 mr-1 inline-block size-4 align-middle" name={iconName} />
      {label}
    </span>
  );
};

export default TrackAnnotationText;
